import { isEmpty } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useWebSocket from 'react-use-websocket';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import {
  useGetNotificationsQuery,
  usePostMarkReadNotificationsMutation,
} from '../../components/ui/Notification/store/notificationSlice';
import pop from '../../assets/audio/notification.wav';
import {
  commonState,
  setNotificationExpanded,
} from '../../app/base/commonSlice';
import useSocket from './useSocket';

const useNotification = () => {
  const dispatch = useDispatch();
  const [limit, setLimit] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isNewNotificationAvailable, setIsNewNotificationAvailable] =
    useState(false);
  const { isNotificationExpanded: expanded, mobileMenu } =
    useSelector(commonState);

  const isMobile = useMediaQuery({ maxWidth: 767 });

  const { t } = useTranslation();
  const [postMarkReadNotifications] = usePostMarkReadNotificationsMutation();

  const getSocketUrl = useSocket();

  const playNotificationSound = () => {
    const audio = new Audio(pop);
    audio.play();
  };
  // @ts-ignore
  const { lastMessage } = useWebSocket(getSocketUrl);

  const {
    data: notificationData,
    isLoading,
    refetch,
    isSuccess,
  } = useGetNotificationsQuery(
    { limit: limit * 10 },
    {
      skip: isMobile
        ? !(mobileMenu.isMobileNotificationClose || isNewNotificationAvailable)
        : !(expanded || isNewNotificationAvailable),
    }
  );

  const handleButtonClick = async () => {
    dispatch(setNotificationExpanded(!expanded));
    try {
      if (!expanded) {
        await postMarkReadNotifications();
      }
    } catch (err) {
      console.log(err);
    }
  };

  // eslint-disable-next-line consistent-return
  const fetchNextPage = useCallback(() => {
    if (
      notificationData?.results?.notifications.length >= notificationData.count
    ) {
      setHasMore(false);
      return false;
    }
    setLimit(limit + 1);
  }, [
    limit,
    notificationData?.count,
    notificationData?.results?.notifications.length,
  ]);

  useEffect(() => {
    if (
      !isEmpty(lastMessage) &&
      JSON.parse(lastMessage?.data)?.sender === 'Notification'
    ) {
      setIsNewNotificationAvailable(true);
      if (isSuccess) {
        const promise = new Promise((resolve, __reject) => {
          resolve(refetch());
        });
        promise.then((__res) => {
          message.info(`${t('notification.message')}`);
          playNotificationSound();
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, isSuccess, refetch]);

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      const scrollMoreButton = document.querySelector('.scroll-more-button');
      if (
        event.target.closest('.notification-button') ||
        event.target === scrollMoreButton
      ) {
        // Clicked inside the dropdown notification or on the "Scroll to see More" button, do nothing
        return;
      }
      // Clicked outside the dropdown notification, close it
      dispatch(setNotificationExpanded(false));
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      //
    };
  }, [dispatch]);

  return {
    fetchNextPage,
    hasMore,
    handleButtonClick,
    notifications: notificationData?.results?.notifications,
    notificationData,
    isLoading,
    expanded,
  };
};

export default useNotification;
